// ─────────────────────────────────────────────────────────────
// ScoreRing — animated ATS score reveal component
// Drop into DashboardPage.jsx above the export default
//
// Usage:
//   <ScoreRing score={result.ats_score} animate={true} />
//
// Props:
//   score    — number 0-100
//   animate  — boolean, defaults true. Set false to skip animation.
//   size     — ring diameter in px, defaults 160
// ─────────────────────────────────────────────────────────────

import { useState, useEffect, useRef } from "react";

export default function ScoreRing({ score = 0, animate = true, size = 160 }) {
  const [displayed, setDisplayed] = useState(animate ? 0 : score);
  const rafRef = useRef(null);
  const RADIUS = size * 0.4125;         // ~66px at size=160
  const CIRC   = 2 * Math.PI * RADIUS;
  const DURATION = 1800;                // ms

  // colour thresholds — matches dashboard scoreColor logic
  const color = displayed >= 75 ? "#0F6E56"   // green
              : displayed >= 50 ? "#854F0B"   // amber
              :                   "#A32D2D";  // red

  const label = displayed >= 75 ? "Strong match"
              : displayed >= 50 ? "Fair match — gaps to close"
              :                   "Needs improvement";

  const offset = CIRC - (displayed / 100) * CIRC;

  useEffect(() => {
    if (!animate) { setDisplayed(score); return; }
    cancelAnimationFrame(rafRef.current);
    setDisplayed(0);
    const start = performance.now();

    function step(now) {
      const t    = Math.min((now - start) / DURATION, 1);
      const ease = t < 0.5 ? 2 * t * t : -1 + (4 - 2 * t) * t;
      setDisplayed(Math.round(score * ease));
      if (t < 1) rafRef.current = requestAnimationFrame(step);
    }
    rafRef.current = requestAnimationFrame(step);
    return () => cancelAnimationFrame(rafRef.current);
  }, [score, animate]);

  return (
    <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 8 }}>
      {/* Ring */}
      <div style={{ position: "relative", width: size, height: size }}>
        <svg
          width={size}
          height={size}
          viewBox={`0 0 ${size} ${size}`}
          style={{ transform: "rotate(-90deg)" }}
        >
          {/* Track */}
          <circle
            cx={size / 2} cy={size / 2} r={RADIUS}
            fill="none" stroke="#e2e8f0" strokeWidth={size * 0.075}
          />
          {/* Progress arc */}
          <circle
            cx={size / 2} cy={size / 2} r={RADIUS}
            fill="none"
            stroke={color}
            strokeWidth={size * 0.075}
            strokeLinecap="round"
            strokeDasharray={CIRC}
            strokeDashoffset={offset}
            style={{ transition: "stroke 0.4s ease" }}
          />
        </svg>

        {/* Centre text */}
        <div style={{
          position: "absolute", inset: 0,
          display: "flex", flexDirection: "column",
          alignItems: "center", justifyContent: "center",
          gap: 2,
        }}>
          <span style={{
            fontSize: size * 0.3,
            fontWeight: 800,
            lineHeight: 1,
            color,
            letterSpacing: "-0.04em",
            transition: "color 0.4s ease",
            fontFamily: "'Inter', -apple-system, sans-serif",
          }}>
            {displayed}
          </span>
          <span style={{ fontSize: size * 0.075, color: "#94a3b8", fontWeight: 500 }}>/100</span>
        </div>
      </div>

      {/* Label below ring */}
      <div style={{
        fontSize: 13,
        fontWeight: 600,
        color,
        transition: "color 0.4s ease",
        textAlign: "center",
      }}>
        {label}
      </div>
    </div>
  );
}
